import React, { useEffect, useState, useCallback } from 'react';
import { SessionLog, sessionService } from '../services/sessionService';
import { format } from 'date-fns';
import { supabase } from '../config/supabase';

interface SessionsControlProps {
  isSupervisor: boolean;
}

const ITEMS_PER_PAGE = 5;

const SessionsControl: React.FC<SessionsControlProps> = ({ isSupervisor }) => {
  const [sessions, setSessions] = useState<SessionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSessions, setTotalSessions] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState({
    userId: '',
    startDate: '',
    endDate: '',
    deviceType: '',
    osName: '',
    browserName: '',
    sessionStatus: 'active' as 'active' | 'closed' | 'all',
  });

  const loadSessions = useCallback(async () => {
    if (!isSupervisor) return; // Solo cargar si es supervisor
    
    try {
      setLoading(true);
      const response = await sessionService.getSessionLogs({
        ...filters,
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
      });
      setSessions(response.data);
      setTotalSessions(response.count);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, isSupervisor]);

  // Configurar suscripción a cambios en session_logs
  useEffect(() => {
    if (!isSupervisor) {
      console.log('[SessionsControl] No es supervisor, no se configura suscripción');
      return;
    }
    
    console.log('[SessionsControl] Setting up Supabase realtime subscription for session_logs...');
    
    const channel = supabase.channel('session_logs_channel');
    
    channel
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'session_logs'
        },
        (payload) => {
          console.log('[SessionsControl] Change detected:', {
            type: payload.eventType,
            schema: payload.schema,
            table: payload.table,
            payload
          });
          loadSessions();
        }
      )
      .subscribe((status) => {
        console.log('[SessionsControl] Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          // Verificar que la suscripción está activa
          const channelState = channel.state;
          console.log('[SessionsControl] Channel state after subscription:', channelState);
          console.log('[SessionsControl] Successfully subscribed to session_logs changes');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[SessionsControl] Error subscribing to session_logs changes');
        }
      });

    // Cargar datos iniciales
    console.log('[SessionsControl] Loading initial session logs...');
    loadSessions();

    return () => {
      console.log('[SessionsControl] Cleaning up subscription...');
      supabase.removeChannel(channel);
    };
  }, [isSupervisor, loadSessions]);

  // Efecto para manejar cambios en filtros y paginación
  useEffect(() => {
    if (isSupervisor) {
      console.log('Loading sessions due to filter/page change:', {
        filters,
        currentPage
      });
      loadSessions();
    }
  }, [filters, currentPage, isSupervisor, loadSessions]);

  const handleForceLogout = async (sessionId: string) => {
    try {
      await sessionService.forceLogout(sessionId);
      await loadSessions();
    } catch (error) {
      console.error('Error forcing logout:', error);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(0); // Resetear a la primera página cuando cambian los filtros
  };

  const formatDate = (date: string) => {
    return format(new Date(date), "dd/MM/yyyy HH:mm:ss");
  };

  const totalPages = Math.ceil(totalSessions / ITEMS_PER_PAGE);

  return (
    <div className="bg-white shadow-lg rounded-lg p-6">
      <h2 className="text-2xl font-bold mb-6">Control de Sesiones</h2>

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha Inicio
          </label>
          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha Fin
          </label>
          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Dispositivo
          </label>
          <select
            name="deviceType"
            value={filters.deviceType}
            onChange={handleFilterChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">Todos</option>
            <option value="desktop">PC Escritorio</option>
            <option value="laptop">Portátil</option>
            <option value="mobile">Móvil</option>
            <option value="tablet">Tableta</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estado de Sesión
          </label>
          <select
            name="sessionStatus"
            value={filters.sessionStatus}
            onChange={handleFilterChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="active">Activas</option>
            <option value="closed">Cerradas</option>
            <option value="all">Todas</option>
          </select>
        </div>
      </div>

      {/* Tabla de sesiones */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuario / Dispositivo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sistema / Navegador
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                IP / Ubicación
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Inicio - Cierre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              {isSupervisor && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center">
                  <div className="flex justify-center items-center">
                    <svg className="animate-spin h-5 w-5 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Cargando...
                  </div>
                </td>
              </tr>
            ) : sessions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No hay sesiones que mostrar
                </td>
              </tr>
            ) : (
              sessions.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900">
                        {session.profiles?.name || 'Usuario Desconocido'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {session.profiles?.email}
                      </div>
                      <span className={`mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${
                        session.device_type === 'desktop' 
                          ? 'bg-purple-100 text-purple-800'
                          : session.device_type === 'laptop'
                          ? 'bg-blue-100 text-blue-800'
                          : session.device_type === 'mobile'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {session.device_type === 'desktop' ? 'PC Escritorio' :
                         session.device_type === 'laptop' ? 'Portátil' :
                         session.device_type === 'mobile' ? 'Móvil' :
                         session.device_type === 'tablet' ? 'Tableta' :
                         session.device_type}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {session.os_name} {session.os_version}
                    </div>
                    <div className="text-sm text-gray-500">
                      {session.browser_name} {session.browser_version}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{session.ip_address}</div>
                    {session.location && (
                      <div className="text-sm text-gray-500">
                        {`${session.location.city}, ${session.location.region}`}
                        <br />
                        {session.location.country}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {formatDate(session.login_timestamp)}
                    </div>
                    {session.logout_timestamp && (
                      <div className="text-sm text-gray-500">
                        {formatDate(session.logout_timestamp)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        session.logout_timestamp
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {session.logout_timestamp ? 'Cerrada' : 'Activa'}
                    </span>
                  </td>
                  {isSupervisor && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!session.logout_timestamp && (
                        <button
                          onClick={() => handleForceLogout(session.id)}
                          className="text-red-600 hover:text-red-900 flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Cerrar Sesión
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {!loading && sessions.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <div className="flex justify-between w-full">
            <div className="text-sm text-gray-700">
              Mostrando{' '}
              <span className="font-medium">
                {currentPage * ITEMS_PER_PAGE + 1}
              </span>{' '}
              a{' '}
              <span className="font-medium">
                {Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalSessions)}
              </span>{' '}
              de <span className="font-medium">{totalSessions}</span> resultados
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage >= totalPages - 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  Siguiente
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsControl;
